import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    useGetPromoterQuery,
    useCreatePromoterMutation,
    useUpdatePromoterMutation,
} from 'api/main';
import { CustomTextField } from 'components';
import Joi from 'joi';
import ImageUpload from 'components/ImageUpload/ImageUpload';
import { Box, Typography } from '@mui/material';
import { TunnelBox } from '../tunnel/common';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import { validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';
import { useGetDocURLById } from 'utils/hooks';
import { ActionButton, CancelButton, Loader } from '../../components';

const PromoterFormSchema = {
    name: Joi.string().trim().required().min(2).max(120).messages({
        'string.empty': texts.SAISIR_LE_CHAMP,
        'string.min': texts.SAISIR_LE_CHAMP,
        'string.max': texts.SAISIR_LE_CHAMP,
    }),
    logoFile: Joi.any().invalid(null).messages({
        'any.invalid': texts.SAISIR_LE_CHAMP,
    }),
};

const hasUniqueNameViolation = error => {
    const nameErrors = error?.data?.violations.find(v => v.propertyPath === 'name');
    return nameErrors?.message === 'already_used';
};
const hasNameCharacterViolation = error => {
    const nameErrors = error?.data?.violations.find(v => v.propertyPath === 'name');
    return nameErrors?.message === 'illegal_character';
};

const Promoter = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [errors, setErrors] = useState({});

    const { data: promoter, isLoading } = useGetPromoterQuery(id || '');

    const [form, setForm] = useState({
        name: '',
        logoFile: null,
        logo: null,
    });
    const [
        createPromoter,
        { isLoading: createLoader, isSuccess: createSuccess, error: createError },
    ] = useCreatePromoterMutation();

    const [
        updatePromoter,
        { isLoading: updateLoader, isSuccess: updateSuccess, error: updateError },
    ] = useUpdatePromoterMutation();

    const isEditMode = Boolean(id);

    useEffect(() => {
        if (promoter) {
            setForm(prevState => ({
                ...prevState,
                ...promoter,
            }));
        }
    }, [promoter]);

    const { src: logoUrl } = useGetDocURLById(promoter?.logo ? promoter.logo.id : null);

    useEffect(() => {
        if (logoUrl) {
            fetch(logoUrl)
                .then(response => response.blob())
                .then(blob => {
                    setForm(prevState => ({
                        ...prevState,
                        logoFile: new File([blob], 'logo.png', { type: blob.type }),
                    }));
                });
        }
    }, [logoUrl]);

    const handleChange = key => e => {
        const { value } = e.target;
        setForm(oldValue => ({ ...oldValue, [key]: value }));
        setErrors(prevState => ({ ...prevState, [key]: '' }));
    };

    const handleLogoChange = async e => {
        const file = e.target.files[0];
        if (file) {
            setForm(prevState => ({ ...prevState, logoFile: file, logo: null }));
        }
    };

    const handleRemoveLogo = () => {
        setForm(prevState => ({ ...prevState, logoFile: null, logo: null }));
    };

    const handleSubmit = useCallback(() => {
        const trimmedForm = {
            ...form,
            name: form.name.trim(),
        };
        const { valid, newErrors } = validateSchema(PromoterFormSchema, trimmedForm);
        if (valid) {
            setErrors({
                logoFile: '',
                name: '',
            });
            if (isEditMode) {
                updatePromoter(trimmedForm);
            } else {
                createPromoter(trimmedForm);
            }
        } else {
            setErrors(newErrors);
        }
    }, [createPromoter, updatePromoter, form, isEditMode]);

    const goBack = useCallback(() => {
        dispatch(push('/promoters'));
    }, [dispatch]);

    useEffect(() => {
        if (createSuccess || updateSuccess) {
            goBack();
        }
    }, [createSuccess, updateSuccess, goBack]);

    useEffect(() => {
        if (createError || updateError) {
            if (hasUniqueNameViolation(createError ?? updateError)) {
                setErrors(prevState => ({ ...prevState, name: texts.PROMOTER_NAME_EXISTS }));
            } else if (hasNameCharacterViolation(createError ?? updateError)) {
                setErrors(prevState => ({ ...prevState, name: texts.PROMOTER_NAME_INVALID }));
            }
        }
    }, [createError, updateError]);

    return (
        <Loader
            loading={isLoading || createLoader || updateLoader}
            size={25}
            marginTop={0}
            color="inherit"
        >
            <Typography variant="h5" align="left" gutterBottom>
                {isEditMode ? texts.PROMOTER_EDIT : texts.PROMOTER_CREATE}
            </Typography>
            <TunnelBox sx={{ marginTop: '30px' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: {
                            xl: '50%',
                            lg: '80%',
                            md: '100%',
                        },
                        margin: '0 auto',
                    }}
                >
                    <CustomTextField
                        fullWidth
                        label={texts.PROMOTER_NAME}
                        value={form.name}
                        onChange={handleChange('name')}
                        error={errors.name}
                    />
                    <ImageUpload
                        type="image"
                        id="promoter-logo"
                        width="100%"
                        label={texts.PROMOTER_LOGO}
                        ContainerProps={{
                            width: '100%',
                            marginTop: 0,
                            marginBottom: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                        selectedFiles={form.logoFile ? [form.logoFile] : []}
                        handleFileChange={handleLogoChange}
                        removeFile={handleRemoveLogo}
                        error={errors.logoFile}
                    />
                </Box>
            </TunnelBox>
            <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
                <CancelButton disabled={createLoader} onClick={goBack}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={createLoader}
                    loading={createLoader}
                >
                    {texts.SAVE}
                </ActionButton>
            </Box>
        </Loader>
    );
};

export default Promoter;
